/**
 * Hono Integration
 * Lightweight Hono middleware for x402 payments with Coinbase compatibility
 */

import { MiddlewareHandler } from 'hono'
import { X402Middleware } from '../middleware'
import { X402Client } from '../../client/fetch'
import { MiddlewareOptions, FacilitatorConfig } from '../../lib'

export interface X402HonoMiddleware {
  (options: MiddlewareOptions): MiddlewareHandler
}


export function createHonoMiddleware(client: X402Client): X402HonoMiddleware {
  const middleware = new X402Middleware(client)

  return (options: MiddlewareOptions): MiddlewareHandler => {
    return async (c, next): Promise<Response | void> => {
      try {
        // Check if this is a payment request (has X-PAYMENT header)
        const xPaymentHeader = c.req.header('x-payment') || c.req.header('X-PAYMENT')

        if (!xPaymentHeader) {
          // No payment header - return 402 with payment requirements
          const requirements = middleware['createPaymentRequirements'](options)
          return c.json(requirements, 402)
        }

        // Has payment header - verify and settle payment
        const settlement = await middleware['processPayment'](xPaymentHeader, options)

        if (settlement && c) {
          // Add settlement info to context
          c.set('x402Settlement', {
            txHash: settlement.txHash || '',
            networkId: settlement.networkId || '',
            payer: settlement.payer,
            gasUsed: settlement.gasUsed
          })
        }

        // Continue to next handler
        await next()

      } catch (error) {
        c.status(500)
        return c.json({
          error: 'Internal server error'
        })
      }
    }
  }
}


/**
 * Convenience function for Hono users (legacy single-route support)
 */
export function withX402(clientOrConfig: X402Client | FacilitatorConfig, options?: MiddlewareOptions) {
  return createHonoMiddleware(
    clientOrConfig instanceof X402Client ? clientOrConfig : new X402Client(clientOrConfig)
  )(options!)
}


// Type augmentation for Hono Context
declare module 'hono' {
  interface ContextVariableMap {
    x402Settlement?: {
      txHash: string
      networkId: string
      payer?: string
      gasUsed: any
    }
  }
}
